import { useState, useEffect } from 'react'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/client'
import { useCart } from '../useCart'
import { useCheckout } from '../../../modules/checkout/hooks/useCheckout'
import { loadScript } from 'lib/core/resources/helpers'
import { KlarnaWidget, KlarnaWidgetProps } from './components/KlarnaWidget'

interface KlarnaKeys extends Object {
  clientToken?: string
  paymentMethodCategory?: string
  code?: string
}

interface CreateKlarnaTokenResponse {
  createKlarnaToken: {
    client_token: string
    code: string
    payment_method_category: string
  }
}

export type UseKlarna = () => {
  clientToken: string | undefined
  paymentMethodCategory: string | undefined
  code: string | undefined
  isLoading: boolean
  KlarnaWidget: (props: KlarnaWidgetProps) => JSX.Element | null
}

export const useKlarna: UseKlarna = () => {
  const { cartId, getCartIsLoading } = useCart()
  const { shippingAddress, billingAddress, shippingMethod } = useCheckout()
  const [keysAreLoading, setKeysAreLoading] = useState(true)
  const [keys, setKeys] = useState<KlarnaKeys>({})
  const [createKlarnaToken] = useMutation<CreateKlarnaTokenResponse>(createKlarnaTokenMutation)
  const [intervalId, setIntervalId] = useState<number | undefined>(undefined)
  const [apiIsLoading, setApiIsLoading] = useState(!getIsKlarnaLoaded())
  const isKlarnaLoaded = getIsKlarnaLoaded()

  const shouldCreateToken = !shippingAddress.isFetching &&
    !billingAddress.isFetching &&
    !shippingMethod.isFetching &&
    !getCartIsLoading()

  useEffect(() => {
    if (shouldCreateToken) {
      setKeysAreLoading(true)
      createKlarnaToken({ variables: { cartId } }).then(res => {
        setKeys({
          clientToken: res.data?.createKlarnaToken.client_token,
          paymentMethodCategory: res.data?.createKlarnaToken.payment_method_category,
          code: res.data?.createKlarnaToken.code
        })
        setKeysAreLoading(false)
      }).catch(() => {
        // TODO: log error to sentry
        setKeysAreLoading(false)
      })
    }
  }, [
    cartId,
    shouldCreateToken,
    createKlarnaToken,
    setKeys,
    setKeysAreLoading
  ])

  useEffect(() => {
    loadExternalResources()
  }, [])

  useEffect(() => {
    function checkKlarna () {
      if (getIsKlarnaLoaded()) {
        setApiIsLoading(false)
      }
    }

    if (!intervalId && !isKlarnaLoaded) {
      const id = window.setInterval(checkKlarna, CHECK_KLARNA_API_LOADED_INTERVAL)
      setIntervalId(id)
    }

    if (isKlarnaLoaded) {
      window.clearInterval(intervalId)
    }

    return () => {
      window.clearInterval(intervalId)
    }
  }, [
    isKlarnaLoaded,
    intervalId,
    setIntervalId,
    setApiIsLoading
  ])

  return {
    clientToken: keys.clientToken,
    paymentMethodCategory: keys.paymentMethodCategory,
    code: keys.code,
    isLoading: keysAreLoading || apiIsLoading,
    KlarnaWidget
  }
}

// @ts-expect-error
const getIsKlarnaLoaded = () => window.Klarna?.Payments

const loadExternalResources = () => {
  if (!getIsKlarnaLoaded()) {
    const scripts = [
      'https://x.klarnacdn.net/kp/lib/v1/api.js',
      // TODO: set `uci` and `country` as parameters.
      'https://us-library.klarnaservices.com/merchant.js?uci=6ed9422d-4883-497a-9c2f-9cf886a52de3&country=US'
    ]

    scripts.forEach(src => loadScript({ src }))
  }
}

export const createKlarnaTokenMutation = gql`
  mutation createKlarnaToken(
    $cartId: String
  ) {
    createKlarnaToken(
      cart_id: $cartId
    ) {
      client_token
      code
      payment_method_category
    }
  }
`

const CHECK_KLARNA_API_LOADED_INTERVAL = 500
